import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { getDeviceTimeZoneIdSelector, selectSensorDeviceByIdSelector } from '@redux/device/deviceSelectors';
import { getPersonVitalsByPersonIdSelector, getSelectedPersonDetailsSelector, getSelectedPersonVitalOverview } from '@redux/person/personSelector';
import { getPersonVitalOverviewThunk } from '@redux/person/personThunks';
import { getPersonUpcomingTasksSelector } from '@redux/tasks/tasksSelector';
import { getPersonUpcomingTasksThunk } from '@redux/tasks/tasksThunks';
import { ReduxState } from '@redux/types';
import { Button } from 'components/Button/Button';
import { DropDownMenu } from 'components/DropDownMenu/DropDownMenu';
import { ArrowsCounterClockwiseIcon, PencilSimpleLineIcon, RepeatIcon, WaveformIcon } from 'components/Icons';
import { PatientDeviceMode } from 'components/PatientDeviceMode/PatientDeviceMode';
import { Text } from 'components/Typography';
import { Color } from 'utils/color';
import { dateStringFromISOWithTz, monthStringFromISOWithTz, smartDateTimeFromISOWithTz } from 'utils/time';
import { getInitials } from 'utils/util';
import style from './SelectedPatientDetails.scss';

type SelectedPatientDetailsProps = {
  hasPreselectedPatient?: boolean;
  patientId: Nullable<string>;
  patientTimeZone: string;
  clearSelectedPatient: () => void;
};

type Props = ConnectedProps<typeof connectRedux> & SelectedPatientDetailsProps;

const SelectedPatientDetails: React.FC<Props> = ({
  patientId,
  patientTimeZone,
  clearSelectedPatient,
  getPersonVitalOverview,
  getPersonUpcomingTasks,
  personVitalsByPersonIdSelector,
  selectedPersonVitalOverview,
  selectedPersonDetails,
  sensorDeviceById,
  personUpcomingTasks,
  hasPreselectedPatient = false
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [patientTime, setPatientTime] = useState('');
  const personVitals = patientId ? personVitalsByPersonIdSelector[patientId] : null;
  const { deviceStatus, personPresence, personPresenceLastReading } = personVitals || {};
  const sensorDeviceDetails = selectedPersonDetails?.sensorDeviceId ? sensorDeviceById[selectedPersonDetails.sensorDeviceId] : undefined;

  useEffect(() => {
    const updateTime = () => {
      const now = DateTime.now().setZone(patientTimeZone);
      const formatted = now.toFormat('h:mma') + ` (${now.offsetNameShort})`;
      setPatientTime(formatted);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000); // update every minute
    return () => clearInterval(interval);
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      getPersonUpcomingTasks(patientId);
    }
  }, [patientId]);

  const navigate = useNavigate();
  const onPressOpenDashboard = useCallback(() => {
    navigate(`/patients/${patientId}`);
  }, [patientId, navigate]);

  const onEditPatientClick = useCallback(() => {
    navigate(`/patients/${patientId}/editPatient`);
  }, [patientId, navigate]);

  useEffect(() => {
    const fetchPersonVitalOverview = async () => {
      if (patientId) {
        await getPersonVitalOverview(patientId, selectedDate, patientTimeZone);
      }
    };

    fetchPersonVitalOverview();
  }, [patientId, selectedDate]);

  const getScheduledMonth = (scheduledTime: string) => {
    return monthStringFromISOWithTz(scheduledTime, patientTimeZone);
  };

  const getScheduledDate = (scheduledTime: string) => {
    return dateStringFromISOWithTz(scheduledTime, patientTimeZone);
  };

  const formatSmartDateTime = (scheduledTime: string): string => {
    return smartDateTimeFromISOWithTz(scheduledTime, patientTimeZone);
  };

  return (
    <>
      <div className={style.selectedPatientHeader}>
        <div className={style.selectedPatientInfo}>
          <div className={style.avatar}>
            <Text variant="2xl" weight={500} className={style.initials}>
              {getInitials(selectedPersonVitalOverview?.personName || '')}
            </Text>
          </div>
          <Text variant="xl">{selectedPersonVitalOverview?.personName}</Text>
          <Tooltip title={selectedPersonVitalOverview?.email} followCursor placement="bottom">
            <div className={style.textWrapper}>
              <Text variant="lg" className={classNames(style.patientEmail, style.ellipsisText)}>
                {selectedPersonVitalOverview?.email}
              </Text>
            </div>
          </Tooltip>
        </div>
        <div className={style.selectedPatientActions}>
          <DropDownMenu
            menuText="Actions"
            menuItems={[
              !hasPreselectedPatient && {
                text: 'Change patient',
                icon: ArrowsCounterClockwiseIcon,
                onClick: clearSelectedPatient
              },
              {
                text: 'Edit patient details',
                icon: PencilSimpleLineIcon,
                onClick: onEditPatientClick
              }
            ]}
          />
          {!hasPreselectedPatient && (
            <Button variant="outlined" onClick={onPressOpenDashboard}>
              Open Dashboard
            </Button>
          )}
        </div>
      </div>
      <div className={style.selectedPatientStatusDetails}>
        {false && (
          <div className={style.engagementPlansContainer}>
            <Text variant="nlg">Engagement plans</Text>
          </div>
        )}
        <div className={style.upcomingTasksContainer}>
          <Text variant="nlg">Upcoming tasks</Text>
          {personUpcomingTasks?.slice(0, 4).map((value) => (
            <div key={value.taskId} className={style.upcomingTaskContainer}>
              <div className={style.upcomingTaskDate}>
                <Text variant="xs" className={style.dateText}>
                  {getScheduledMonth(value.scheduledForDateTime)}
                </Text>
                <Text variant="xs" className={style.dateText}>
                  {getScheduledDate(value.scheduledForDateTime)}
                </Text>
              </div>
              <div className={style.upcomingTaskDetails}>
                <Text variant="md">{value.taskName}</Text>
                <div className={style.upcomingTaskProperties}>
                  <WaveformIcon color={Color.GRAY}></WaveformIcon>
                  <Text variant="sm">{value.taskType} • </Text>
                  {value.frequency != "Doesn't Repeat" && <RepeatIcon color={Color.GRAY}></RepeatIcon>}
                  <Text variant="sm">{formatSmartDateTime(value.scheduledForDateTime)}</Text>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={style.detailsFooter}>
          <Text variant="md">Patient's time: {patientTime}</Text>
          <PatientDeviceMode deviceMode={sensorDeviceDetails?.deviceMode} deviceStatus={deviceStatus} personPresence={personPresence} lastReadingTime={personPresenceLastReading} />
        </div>
      </div>
    </>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    getDeviceTimeZoneId: (personId: string) => getDeviceTimeZoneIdSelector(personId, state),
    selectedPersonVitalOverview: getSelectedPersonVitalOverview(state),
    personVitalsByPersonIdSelector: getPersonVitalsByPersonIdSelector(state),
    personUpcomingTasks: getPersonUpcomingTasksSelector(state),
    selectedPersonDetails: getSelectedPersonDetailsSelector(state),
    sensorDeviceById: selectSensorDeviceByIdSelector(state)
  }),
  {
    getPersonVitalOverview: getPersonVitalOverviewThunk,
    getPersonUpcomingTasks: getPersonUpcomingTasksThunk
  }
);

export default compose(connectRedux)(SelectedPatientDetails);
